import { z } from 'zod';

/**
 * Friend / connection model (Cycle-1 SG-3, D-036). Parents-only, child-safety
 * first: accounts are adults; friend requests are by EXACT EMAIL ONLY (no user
 * search / directory / discovery of minors). Mutual opt-in; block/remove always
 * available.
 *
 * Storage: `dgm-{env}-connections` table as an adjacency list — one mirrored
 * item per direction so each user lists their own connections in a single Query:
 *   pk = userSub, sk = `friend#{otherSub}`
 * A request writes two items (requester `pending_out` + target `pending_in`);
 * accept flips both to `accepted` (TransactWrite); decline/remove deletes both;
 * block sets the blocker's edge to `blocked` and removes the reverse.
 */

export const ConnectionStatus = z.enum(['pending_out', 'pending_in', 'accepted', 'blocked']);
export type ConnectionStatus = z.infer<typeof ConnectionStatus>;

export const ConnectionSchema = z.object({
  /** Owner of this edge (the partition). */
  userSub: z.string().min(1),
  /** The other party. */
  otherSub: z.string().min(1),
  /** The other party's email (denormalized for display without a user lookup). */
  otherEmail: z.string().email(),
  status: ConnectionStatus,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type Connection = z.infer<typeof ConnectionSchema>;

/** Send a friend request by exact email (the only discovery mechanism). */
export const SendRequestInput = z.object({
  email: z.string().email(),
});
export type SendRequestInput = z.infer<typeof SendRequestInput>;
